
import { GoogleGenAI } from "@google/genai";

const IMAGE_MODEL = 'gemini-2.5-flash-image';
const TEXT_MODEL = 'gemini-3-flash-preview';

/**
 * Converts a base64 data URL to a Blob object for Supabase Storage uploading.
 */
export function base64ToBlob(base64: string): Blob {
  const [header, data] = base64.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < array.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}

/**
 * Generates an absurd international "Brainrot" name based on the scene description.
 * Interprets the visual elements, translates to a random language, and adds slang.
 */
export async function generateBrainrotName(prompt: string): Promise<string> {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  
  const systemInstruction = `You are a linguistic brainrot specialist for Gen Alpha.
  Your task is to name a meme based on its visual description.
  
  RULES:
  1. Pick a RANDOM language that uses the Latin (A-Z) alphabet (e.g., Italian, Portuguese, German, French, Dutch, Spanish, Romanian).
  2. DO NOT simply use the Theme or Mod names (e.g., if the mod is "Pizza Rain", don't just say "Pizza"). 
  3. INSTEAD, interpret the whole scene and translate the core concept into that language (e.g., "The Crying King of Cheese" -> "El Rey de Queso Triste").
  4. THEN, blend one or two "Gen Alpha" brainrot words (Skibidi, Rizz, Sigma, Gyatt, Ohio, Mewing, Fanum) into the name using the phonetics of the chosen language.
  5. RETURN ONLY THE NAME. NO QUOTES. NO ENGLISH (unless part of a slang word). 1-3 words max.`;

  try {
    const response = await ai.models.generateContent({
      model: TEXT_MODEL,
      contents: "Generate a descriptive global brainrot name for this scene: " + prompt,
      config: {
        systemInstruction: systemInstruction,
        temperature: 1.0,
      }
    });

    return response.text?.trim().replace(/['"]+/g, '') || "Misterio Rot";
  } catch (error) {
    return "Aura Inconnue";
  }
}

/**
 * Generates a short funny lore backstory for the meme.
 */
export async function generateBrainrotStory(prompt: string): Promise<string> {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  
  const systemInstruction = `You are a Brainrot Historian.
  Write a short, funny 1-2 sentence "Lore" or "Backstory" for a meme based on its description.
  
  RULES:
  1. Use Gen Alpha slang (Skibidi, Rizz, Sigma, Aura, Fanum Tax, Mewing, Ohio, etc.).
  2. Make it sound like a fake historical fact or absolute chaos.
  3. Keep it safe for 8-year-olds (silly, not mean).
  4. RETURN ONLY THE STORY. NO QUOTES. 2 sentences max.
  Example: "This sigma was born in Ohio and has 50,000 aura. He once Fanum Taxed an entire pizza factory just to mew."`;

  try {
    const response = await ai.models.generateContent({
      model: TEXT_MODEL,
      contents: "Write the lore for this meme scene: " + prompt,
      config: {
        systemInstruction: systemInstruction,
        temperature: 0.9,
      }
    });

    return response.text?.trim().replace(/['"]+/g, '') || "Lore unknown. Too much aura detected.";
  } catch (error) {
    return "The lore for this rot was lost in the Ohio riots.";
  }
}

/**
 * Generates an image using the Gemini API.
 */
export async function generateBrainrotImage(prompt: string, retries = 1): Promise<string> {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  const visualPrompt = prompt.replace(/brainrot/gi, 'surreal whimsical');

  try {
    const response = await ai.models.generateContent({
      model: IMAGE_MODEL,
      contents: { 
        parts: [{ 
          text: `PROMPT: ${visualPrompt}. STYLE: High-detail cinematic digital art, vibrant colors. SAFETY: Ensure the character is fully and modestly dressed. No anatomical suggestiveness, bulges, or private parts should be visible. Use creative coverings like loincloths, fabric drapes, or armor that fit the scene. This is for an 8-year-old audience.` 
        }] 
      },
      config: {
        imageConfig: { aspectRatio: "1:1" }
      },
    });

    const candidate = response.candidates?.[0];
    if (!candidate?.content?.parts) throw new Error("No image data provided.");

    for (const part of candidate.content.parts) {
      if (part.inlineData) return `data:image/png;base64,${part.inlineData.data}`;
    }
    
    throw new Error("No image data found.");
  } catch (error: any) {
    if (retries > 0 && (error.status === 429 || error.status >= 500)) {
       await new Promise(r => setTimeout(r, 2000));
       return generateBrainrotImage(prompt, retries - 1);
    }
    throw error;
  }
}

/**
 * Utility to compress/resize base64 images.
 */
export async function compressMemeImage(base64: string, targetWidth = 800, quality = 0.75): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = targetWidth / img.width;
      canvas.width = Math.min(img.width, targetWidth);
      canvas.height = img.height * (canvas.width / img.width);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error("Canvas context failed"));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = base64;
  });
}
